import { useState } from "react";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { Gift } from "lucide-react";
import { Trans } from "react-i18next";
import {
  useAnswerHungarianPhone,
  useIsWelcomeCouponEnabled,
  useWelcomeCouponAvailability,
  useWelcomeCouponTexts,
} from "@frontend-common/hooks/use-welcome-coupon";
import { Header, Page, PageContent, PageTitle } from "@/components/page";
import { LoadingSpinner } from "@/components/spinner";
import { Button } from "@/components/ui/button";
import {
  WelcomeCouponVerification,
  WelcomeCouponVerified,
} from "@/features/welcome-coupon/welcome-coupon-verification";
import { useLanguage } from "@/hooks/use-language";
import { useToken } from "@/hooks/use-token";
import { AudioSlot } from "@/features/audio/kiosk-audio";

export const Route = createFileRoute("/welcome-coupon-screen")({
  component: WelcomeCouponScreen,
});

const NEXT_SCREEN = "/machine-type-selector-screen";

/**
 * Shown right after a kiosk registration to accounts that can still earn the
 * welcome coupon. Anything else (flags off, Slovak kiosk, not eligible, backend
 * unreachable) goes straight on to machine selection. An account with no
 * number is first asked whether it has a Hungarian one; "no" also goes on to
 * machine selection, where the missing-profile-data prompt can take over.
 */
function WelcomeCouponScreen() {
  const token = useToken();
  const enabled = useIsWelcomeCouponEnabled();
  const texts = useWelcomeCouponTexts(useLanguage());
  const availability = useWelcomeCouponAvailability(token);
  const navigate = useNavigate();
  const [verifiedNumber, setVerifiedNumber] = useState<string | null>(null);
  const offered = availability.data?.available === true;

  if (
    !verifiedNumber &&
    (!token ||
      !enabled ||
      availability.isError ||
      availability.data?.available === false)
  ) {
    return <Navigate to={NEXT_SCREEN} replace />;
  }

  return (
    <Page>
      <Header variant="small" />
      <PageContent className="grid grid-cols-1 gap-y-8 pb-40">
        {verifiedNumber && token ? (
          <>
            <AudioSlot slot="page.welcome-coupon.verified" />
            <WelcomeCouponVerified
              accountKey={token}
              phoneNumber={verifiedNumber}
              onContinue={() => void navigate({ to: NEXT_SCREEN })}
            />
          </>
        ) : !offered || texts.isPending ? (
          <LoadingSpinner />
        ) : availability.data?.askHungarianPhone ? (
          <>
            <AudioSlot slot="page.welcome-coupon.hungarian-phone" />
            <HungarianPhoneQuestion
              onNo={() => void navigate({ to: NEXT_SCREEN })}
            />
          </>
        ) : (
          <>
            <AudioSlot slot="page.welcome-coupon" />
            <Gift className="mx-auto size-28 text-bub-vivid-blue" />
            <PageTitle>{texts.text("title")}</PageTitle>
            <p className="text-center text-3xl">
              {texts.text(
                availability.data?.phoneNumber
                  ? "body_has_number"
                  : "body_no_number",
              )}
            </p>
            <WelcomeCouponVerification
              savedNumber={availability.data?.phoneNumber ?? null}
              onVerified={setVerifiedNumber}
            />
            <p className="text-center text-xl text-gray-600">
              {texts.text("fine_print")}
            </p>
            <button
              type="button"
              className="text-2xl font-bold text-bub-vivid-blue"
              onClick={() => void navigate({ to: NEXT_SCREEN })}
            >
              <Trans>Most kihagyom</Trans>
            </button>
          </>
        )}
      </PageContent>
    </Page>
  );
}

/**
 * Only Hungarian (+36) numbers earn the welcome coupon, so it is not promised
 * before the account says it has one. No skip: the answer decides between
 * the welcome coupon and the missing-profile-data prompt. "Yes" stays here and
 * the refreshed availability shows the offer.
 */
function HungarianPhoneQuestion({ onNo }: { onNo: () => void }) {
  const answer = useAnswerHungarianPhone();
  return (
    <>
      <PageTitle>
        <Trans>Van magyar (+36) telefonszámod?</Trans>
      </PageTitle>
      <div className="flex gap-4">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          disabled={answer.isPending}
          onClick={() => answer.mutate(false, { onSettled: onNo })}
        >
          <Trans>Nem</Trans>
        </Button>
        <Button
          type="button"
          className="flex-1"
          disabled={answer.isPending}
          onClick={() => answer.mutate(true)}
        >
          <Trans>Igen</Trans>
        </Button>
      </div>
    </>
  );
}
